export const WORKFORCE_LANG_META = [
  { code: 'en', name: 'English', sortOrder: 0 },
  { code: 'de', name: 'Deutsch', sortOrder: 1 },
  { code: 'it', name: 'Italiano', sortOrder: 2 },
  { code: 'nl', name: 'Nederlands', sortOrder: 3 },
] as const;

export const WORKFORCE_LANGS = WORKFORCE_LANG_META.map((l) => l.code);

export type WorkforceLang = (typeof WORKFORCE_LANGS)[number];

/**
 * Used when nothing better is known: no stored choice, and a browser whose
 * languages are all outside {@link WORKFORCE_LANGS}.
 */
export const WORKFORCE_DEFAULT_LANG: WorkforceLang = 'en';

/**
 * Last resort when a key is missing in the active language.
 *
 * `en.json` is the catalogue every other file is measured against, so it is the
 * only file that can be relied on to have an entry.
 */
export const WORKFORCE_FALLBACK_LANG: WorkforceLang = 'en';

export function isWorkforceLang(value: unknown): value is WorkforceLang {
  return (
    typeof value === 'string' &&
    (WORKFORCE_LANGS as readonly string[]).includes(value)
  );
}

/**
 * First supported language among the browser's preferences, else
 * {@link WORKFORCE_DEFAULT_LANG}.
 *
 * Region is dropped (`de-CH` counts as `de`): the interface is translated per
 * language, not per region, so a regional variant of a language we have is
 * better served by that language than by falling through to English.
 */
export function resolveBrowserLang(
  preferred: readonly string[] | undefined,
): WorkforceLang {
  for (const entry of preferred ?? []) {
    const lang = entry.split('-')[0]?.toLowerCase();
    if (isWorkforceLang(lang)) {
      return lang;
    }
  }
  return WORKFORCE_DEFAULT_LANG;
}
